import './shop-page.scss';

import NiceModal from '@ebay/nice-modal-react';
import { Gem, ShoppingCart, Star, Zap } from 'lucide-react';
import React, { type CSSProperties, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { useAppSelector } from '@/app/store/hooks';
import { selectWallet } from '@/entities/user';
import DangerTriangleIcon from '@/shared/assets/icons/danger-triangle.svg?react';
import { MODALS } from '@/shared/config/modals';
import { twa } from '@/shared/lib/telegram/twa';
import { type GiftDetailItem, GiftDetailModal } from '@/shared/ui/modals/GiftDetailModal';
import { Search } from '@/shared/ui/Search';
import { Spinner } from '@/shared/ui/Spinner';

import type { InventoryGift, ShopGiftListing } from './api';
import type { GiftFilter, GiftSortBy, ShopTab } from './types';
import { GiftControls } from './ui/GiftControls';
import { InventoryGiftCard } from './ui/InventoryGiftCard';
import { ShopGiftCard } from './ui/ShopGiftCard';
import { ShopTabs } from './ui/ShopTabs';
import { useGiftInventory } from './useGiftInventory';
import { useShopGifts } from './useShopGifts';

interface ShopItem {
  id: number;
  name: string;
  description: string;
  price: number;
  icon: string;
  colorModifier: 'yellow' | 'purple' | 'blue' | 'pink';
}

const shopItems: ShopItem[] = [
  {
    id: 1,
    name: 'Lucky Boost',
    description: '2x win chance for 24h',
    price: 50,
    icon: '⚡',
    colorModifier: 'yellow',
  },
  {
    id: 2,
    name: 'Golden Dice',
    description: 'Premium skin effect',
    price: 100,
    icon: '🎲',
    colorModifier: 'purple',
  },
  {
    id: 3,
    name: 'Profit Shield',
    description: 'Protect 50% of losses',
    price: 75,
    icon: '🛡️',
    colorModifier: 'blue',
  },
  {
    id: 4,
    name: 'Diamond Crown',
    description: 'Exclusive VIP badge',
    price: 200,
    icon: '👑',
    colorModifier: 'pink',
  },
];

export const ShopPage: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const starsWallet = useAppSelector(selectWallet('STARS'));
  const initialTab = (location.state as { tab?: ShopTab } | null)?.tab ?? 'shop';
  const [activeTab, setActiveTab] = useState<ShopTab>(initialTab);
  const [giftSearch, setGiftSearch] = useState('');
  const [giftFilter, setGiftFilter] = useState<GiftFilter>('all');
  const [giftSort, setGiftSort] = useState<GiftSortBy>('price_asc');
  const [invSearch, setInvSearch] = useState('');
  const [detailItem, setDetailItem] = useState<GiftDetailItem | null>(null);

  const { loading, error, buying, buy, getFiltered } = useShopGifts();
  const {
    loading: invLoading,
    error: invError,
    withdrawing,
    withdraw,
    getFiltered: getInventoryFiltered,
  } = useGiftInventory();

  const filteredGifts = useMemo(
    () => getFiltered(giftSearch, giftFilter, giftSort),
    [getFiltered, giftSearch, giftFilter, giftSort],
  );

  const filteredInventory = useMemo(
    () => getInventoryFiltered(invSearch),
    [getInventoryFiltered, invSearch],
  );

  const handlePurchase = async (item: ShopItem) => {
    const confirmed = await twa.showConfirm(`Purchase ${item.name} for ${item.price} stars?`);
    if (confirmed) {
      twa.showAlert(`You purchased ${item.name}!`);
      twa.hapticFeedback.notificationOccurred('success');
    }
  };

  const handleWithdraw = async (item: InventoryGift) => {
    const confirmed = await twa.showConfirm(t('shop.withdrawConfirm'));
    if (!confirmed) return;

    const ok = await withdraw(item.id);
    if (ok) {
      twa.hapticFeedback.notificationOccurred('success');
      twa.showAlert(`✅ ${t('shop.withdrawSuccess')}`);
    } else {
      twa.hapticFeedback.notificationOccurred('error');
      twa.showAlert(`❌ ${t('shop.withdrawError')}`);
    }
  };

  const handleRedeem = async (item: ShopGiftListing) => {
    const confirmed = await twa.showConfirm(
      `${t('shop.redeem')} ${item.name} for ${item.price_ton.toFixed(2)} TON?`,
    );
    if (!confirmed) return;

    const ok = await buy(item.id);
    if (ok) {
      twa.hapticFeedback.notificationOccurred('success');
      twa.showAlert(`✅ ${item.name} ${t('shop.redeemSuccess')}`);
    } else {
      twa.hapticFeedback.notificationOccurred('error');
      twa.showAlert(`❌ ${t('shop.redeemError')}`);
    }
  };

  const handleBuyStars = () => {
    NiceModal.show(MODALS.DEPOSIT);
    twa.hapticFeedback.notificationOccurred('success');
  };

  const handleMegaBundle = async () => {
    const confirmed = await twa.showConfirm('Purchase Mega Bundle for 499 stars?');
    if (confirmed) {
      twa.showAlert('You purchased Mega Bundle!');
      twa.hapticFeedback.notificationOccurred('success');
    }
  };

  return (
    <div className="shop-page">
      <div className="shop-page__header">
        <div className="shop-page__badge shop-page__badge--in">
          <Star className="shop-page__badge-icon" />
          <span className="shop-page__badge-text">{t('shop.premiumItems')}</span>
        </div>
        <h1 className="shop-page__title">{t('shop.shop')}</h1>
        <p className="shop-page__subtitle">{t('shop.boostGameplay')}</p>
      </div>

      <div className="shop-page__balance shop-page__balance--in">
        <div className="shop-page__balance-inner">
          <div className="shop-page__balance-info">
            <p className="shop-page__balance-label">Your Stars</p>
            <div className="shop-page__balance-row">
              <span className="shop-page__balance-value">
                {(starsWallet?.balance ?? 0).toFixed(0)}
              </span>
              <Gem className="shop-page__balance-gem" />
            </div>
          </div>
          <button
            type="button"
            className="shop-page__balance-btn shop-page__balance-btn--tappable"
            onClick={handleBuyStars}>
            <span className="shop-page__balance-btn-text">+ Deposit Stars</span>
          </button>
        </div>
        <div className="shop-page__balance-glow" aria-hidden />
      </div>

      <ShopTabs active={activeTab} onChange={setActiveTab} />

      {activeTab === 'shop' ? (
        <>
          <div className="shop-page__section">
            <h2 className="shop-page__section-title">{t('shop.availableItems')}</h2>
            <div className="shop-page__grid">
              {shopItems.map((item, index) => (
                <div
                  key={item.id}
                  className="shop-page__item shop-page__item--in"
                  style={{ '--item-in-delay': `${0.1 + index * 0.07}s` } as CSSProperties}>
                  <div
                    className={`shop-page__item-icon shop-page__item-icon--${item.colorModifier}`}>
                    {item.icon}
                  </div>
                  <div className="shop-page__item-body">
                    <h3 className="shop-page__item-name">{item.name}</h3>
                    <p className="shop-page__item-desc">{item.description}</p>
                  </div>
                  <div className="shop-page__item-footer">
                    <div className="shop-page__item-price-row">
                      <Star className="shop-page__item-price-icon" />
                      <span className="shop-page__item-price">{item.price}</span>
                    </div>
                    <button
                      type="button"
                      className="shop-page__item-buy shop-page__item-buy--tappable"
                      onClick={() => handlePurchase(item)}>
                      <ShoppingCart className="shop-page__item-buy-icon" />
                    </button>
                  </div>
                  <div
                    className={`shop-page__item-glow shop-page__item-glow--${item.colorModifier}`}
                    aria-hidden
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="shop-page__featured shop-page__featured--in">
            <div className="shop-page__featured-inner">
              <div className="shop-page__featured-head">
                <div>
                  <div className="shop-page__featured-badge-row">
                    <Zap className="shop-page__featured-badge-icon" />
                    <span className="shop-page__featured-badge">{t('shop.limitedOffer')}</span>
                  </div>
                  <h3 className="shop-page__featured-title">{t('shop.megaBundle')}</h3>
                  <p className="shop-page__featured-desc">Get all boosts + 500 bonus stars</p>
                </div>
                <span className="shop-page__featured-emoji" aria-hidden>
                  🎁
                </span>
              </div>
              <div className="shop-page__featured-footer">
                <div className="shop-page__featured-price-row">
                  <span className="shop-page__featured-price">499</span>
                  <Star className="shop-page__featured-price-icon" />
                  <span className="shop-page__featured-price-old">999</span>
                </div>
                <button
                  type="button"
                  className="shop-page__featured-btn shop-page__featured-btn--tappable"
                  onClick={handleMegaBundle}>
                  <ShoppingCart className="shop-page__featured-btn-icon" />
                  <span>{t('shop.buyNow')}</span>
                </button>
              </div>
            </div>
            <div className="shop-page__featured-sparkles" aria-hidden>
              <span className="shop-page__featured-sparkle" />
              <span className="shop-page__featured-sparkle" />
              <span className="shop-page__featured-sparkle" />
            </div>
          </div>
        </>
      ) : activeTab === 'gifts' ? (
        <div className="shop-page__gifts">
          <div className="shop-page__gifts-header">
            <p className="shop-page__gifts-subtitle">{t('shop.giftsSubtitle')}</p>
          </div>

          <Search
            value={giftSearch}
            onChange={setGiftSearch}
            placeholder={t('shop.searchGiftsPlaceholder')}
          />

          <GiftControls
            filter={giftFilter}
            sortBy={giftSort}
            onFilterChange={setGiftFilter}
            onSortChange={setGiftSort}
          />

          {loading ? (
            <div className="shop-page__gifts-loader">
              <Spinner />
            </div>
          ) : error ? (
            <div className="shop-page__gifts-error">
              <DangerTriangleIcon className="shop-page__gifts-error-icon" />
              <p>{error}</p>
            </div>
          ) : filteredGifts.length > 0 ? (
            <div className="shop-page__gifts-grid">
              {filteredGifts.map((gift, idx) => (
                <ShopGiftCard
                  key={gift.id}
                  item={gift}
                  index={idx}
                  buying={buying === gift.id}
                  onRedeem={handleRedeem}
                  onDetail={setDetailItem}
                />
              ))}
            </div>
          ) : (
            <p className="shop-page__gifts-empty shop-page__gifts-empty--in">
              {t('shop.noGiftsFound')}
            </p>
          )}
        </div>
      ) : (
        /* ── Inventory tab ── */
        <div className="shop-page__gifts">
          <div className="shop-page__gifts-header">
            <p className="shop-page__gifts-subtitle">{t('shop.inventorySubtitle')}</p>
          </div>

          <Search
            value={invSearch}
            onChange={setInvSearch}
            placeholder={t('shop.searchGiftsPlaceholder')}
          />

          {invLoading ? (
            <div className="shop-page__gifts-loader">
              <Spinner />
            </div>
          ) : invError ? (
            <div className="shop-page__gifts-error">
              <DangerTriangleIcon className="shop-page__gifts-error-icon" />
              <p>{invError}</p>
            </div>
          ) : filteredInventory.length > 0 ? (
            <div className="shop-page__gifts-grid">
              {filteredInventory.map((gift, idx) => (
                <InventoryGiftCard
                  key={gift.id}
                  item={gift}
                  index={idx}
                  withdrawing={withdrawing === gift.id}
                  onWithdraw={handleWithdraw}
                  onDetail={setDetailItem}
                />
              ))}
            </div>
          ) : (
            <p className="shop-page__gifts-empty shop-page__gifts-empty--in">
              {t('shop.noInventory')}
            </p>
          )}
        </div>
      )}
      <GiftDetailModal
        open={detailItem !== null}
        onClose={() => setDetailItem(null)}
        item={detailItem}
      />
    </div>
  );
};
